import { computed } from "vue";
import { sensorSpecs, allSensorCodes } from "../lib/sensors";
import { SensorCode } from "@/types/sensor/SensorCode";
import { SensorStates } from "@/types/sensor/SensorStates";
import { useModbusStore } from "@/stores/useModbusStore";

type Page = "charts_page" | "history_page";

export function useStorage(page: Page, chartId: string = "") {
  const modbusStore = useModbusStore();
  function getStorageKeyByGraph(parameter: string): string {
    switch (page) {
      case "history_page":
        return `history_page${modbusStore.portIndex}/settings-${parameter}`;
      case "charts_page":
        return `charts_page${modbusStore.portIndex}/settings-${chartId}${parameter}`;
    }
  }

  function getStorageKeyByPage(parameter: string): string {
    switch (page) {
      case "history_page":
        return `history_page${modbusStore.portIndex}/${parameter}`;
      case "charts_page":
        return `charts_page${modbusStore.portIndex}/${parameter}`;
    }
  }

  const colsKey = computed(() => getStorageKeyByPage("cols"));
  const rowsKey = computed(() => getStorageKeyByPage("rows"));
  const timespanKey = computed(() => getStorageKeyByPage("timespan"));
  const intervalTimeMsKey = computed(() => getStorageKeyByPage("intervaltime"));

  const showDotsKey = computed(() => getStorageKeyByGraph("show_dots"));
  const autoscalingKey = computed(() => getStorageKeyByGraph("autoscaling"));
  const minYKey = computed(() => getStorageKeyByGraph("min_y"));
  const maxYKey = computed(() => getStorageKeyByGraph("max_y"));

  const enabledKeys = computed(() =>
    allSensorCodes.reduce(
      (acc, sensorCode: SensorCode) => {
        acc[sensorCode] = getStorageKeyByGraph(
          `${sensorSpecs[sensorCode].baseStorageKey}_enable`,
        );
        return acc;
      },
      {} as Record<SensorCode, string>,
    ),
  );

  const colorKeys = computed(() =>
    allSensorCodes.reduce(
      (acc, sensorCode: SensorCode) => {
        acc[sensorCode] = getStorageKeyByGraph(
          `${sensorSpecs[sensorCode].baseStorageKey}_color`,
        );
        return acc;
      },
      {} as Record<SensorCode, string>,
    ),
  );

  function setIfNotExists(key: string, value: string | number | boolean) {
    if (localStorage.getItem(key) === null) {
      localStorage.setItem(key, String(value));
    }
  }

  function initStorage() {
    // per-page
    setIfNotExists(colsKey.value, 1);
    setIfNotExists(rowsKey.value, 1);
    setIfNotExists(timespanKey.value, 10);
    setIfNotExists(intervalTimeMsKey.value, 100);

    // per-graph
    setIfNotExists(showDotsKey.value, true);
    setIfNotExists(autoscalingKey.value, false);
    setIfNotExists(minYKey.value, 0);
    setIfNotExists(maxYKey.value, 100);

    for (const sensorCode of allSensorCodes) {
      setIfNotExists(enabledKeys.value[sensorCode], false);
      setIfNotExists(
        colorKeys.value[sensorCode],
        sensorSpecs[sensorCode].defaultColor,
      );
    }
  }

  function getSensorStates(): SensorStates {
    initStorage();
    return allSensorCodes.reduce(
      (acc: SensorStates, sensorCode: SensorCode) => {
        acc[sensorCode] = {
          color: localStorage.getItem(colorKeys.value[sensorCode]) ?? "",
          enabled:
            localStorage.getItem(enabledKeys.value[sensorCode]) === "true",
        };
        return acc;
      },
      {} as SensorStates,
    );
  }

  return {
    getSensorStates,

    colsKey,
    rowsKey,
    timespanKey,
    intervalTimeMsKey,

    showDotsKey,
    autoscalingKey,
    minYKey,
    maxYKey,

    enabledKeys,
    colorKeys,
  };
}
